/**
 * Galería de ficha de producto (WooCommerce).
 * =============================================================================
 *
 * Woo imprime TODAS las fotos como hermanas dentro de:
 *   .woocommerce-product-gallery > .woocommerce-product-gallery__wrapper
 *     > .woocommerce-product-gallery__image  (cada una con data-thumb="url-chica")
 *
 * No usamos FlexSlider (`wc-product-gallery-slider` está desactivado).
 * Este módulo:
 *   1. Deja visible UNA foto grande (clase .is-active).
 *   2. Crea ABAJO un carrusel Swiper de miniaturas NUEVAS (no clona la grande).
 *      Cada thumb es un <button> + <img src="{data-thumb}">.
 *   3. Al hacer clic en un thumb, se muestra la grande del mismo índice.
 *
 * Zoom / lightbox de Woo siguen actuando sobre las imágenes grandes del wrapper.
 *
 * Estilos: src/less/sections/_product-detail.less
 * PHP: woocommerce_gallery_image_size → woocommerce_single (todas a tamaño grande).
 */

/**
 * Fotos grandes que Woo ya pintó en el wrapper (no las thumbs que crea este archivo).
 * @param {Element} gallery
 * @returns {Element[]}
 */
function getSlides(gallery) {
    const wrapper = gallery.querySelector('.woocommerce-product-gallery__wrapper');
    if (!wrapper) {
        return [];
    }
    return Array.from(wrapper.querySelectorAll(':scope > .woocommerce-product-gallery__image'));
}

/**
 * Muestra la foto grande `index` y marca el thumb correspondiente (fade en CSS).
 * También reinicia el zoom de Woo sobre la imagen ahora visible.
 *
 * @param {Element[]} slides
 * @param {Element|null} thumbsRoot
 * @param {number} index
 */
function setActiveSlide(slides, thumbsRoot, index) {
    slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
    });

    if (thumbsRoot) {
        thumbsRoot.querySelectorAll('.product-gallery-thumbs__item').forEach((el, i) => {
            el.classList.toggle('is-active', i === index);
        });
    }

    // El zoom de Woo pone position:relative inline; hay que destruirlo en todas
    // y volver a iniciarlo solo en la .is-active.
    if (typeof window.jQuery === 'undefined') {
        return;
    }
    slides.forEach((slide) => {
        window.jQuery(slide).trigger('zoom.destroy');
    });
    const gallery = slides[0] && slides[0].closest('.woocommerce-product-gallery');
    const pg = gallery && window.jQuery(gallery).data('product_gallery');
    if (pg && typeof pg.initZoomForTarget === 'function') {
        pg.initZoomForTarget(window.jQuery(slides[index]));
    }
}

/**
 * Arma (o re-arma) thumbs + Swiper para una galería.
 * Si Woo regenera el HTML (p. ej. al cambiar variación), se destruye el Swiper anterior.
 *
 * @param {Element} gallery
 */
async function mountGallery(gallery) {
    const slides = getSlides(gallery);
    const existing = gallery.querySelector(':scope > .product-gallery-thumbs');

    // Misma cantidad de slides → ya está montado; evita rehacerlo en cada MutationObserver.
    if (existing && existing.dataset.slideCount === String(slides.length)) {
        return;
    }

    if (existing) {
        if (existing.swiper) {
            existing.swiper.destroy(true, true);
        }
        existing.remove();
    }

    // Woo deja opacity:0 esperando FlexSlider; aquí no hay FlexSlider.
    gallery.style.opacity = '1';
    gallery.classList.add('has-custom-thumbs');

    // Una sola foto: no hace falta carrusel de thumbs.
    if (slides.length < 2) {
        slides.forEach((slide) => {
            slide.classList.add('is-active');
        });
        return;
    }

    setActiveSlide(slides, null, 0);

    const thumbsRoot = document.createElement('div');
    thumbsRoot.className = 'product-gallery-thumbs swiper';
    thumbsRoot.dataset.slideCount = String(slides.length);

    const swiperWrapper = document.createElement('div');
    swiperWrapper.className = 'swiper-wrapper';

    // No cloneNode de la grande: img nueva con la URL chica que Woo ya puso en data-thumb.
    slides.forEach((slide, index) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'swiper-slide product-gallery-thumbs__item' + (index === 0 ? ' is-active' : '');
        item.setAttribute('aria-label', slide.getAttribute('data-thumb-alt') || 'Ver imagen');

        const img = document.createElement('img');
        img.src = slide.getAttribute('data-thumb') || '';
        img.alt = slide.getAttribute('data-thumb-alt') || '';
        img.srcset = slide.getAttribute('data-thumb-srcset') || '';
        img.sizes = slide.getAttribute('data-thumb-sizes') || '';
        item.appendChild(img);

        item.addEventListener('click', () => {
            setActiveSlide(slides, thumbsRoot, index);
            if (thumbsRoot.swiper) {
                thumbsRoot.swiper.slideTo(index);
            }
        });

        swiperWrapper.appendChild(item);
    });

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'product-gallery-thumbs__nav product-gallery-thumbs__prev';
    prev.setAttribute('aria-label', 'Miniaturas anteriores');

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'product-gallery-thumbs__nav product-gallery-thumbs__next';
    next.setAttribute('aria-label', 'Miniaturas siguientes');

    thumbsRoot.appendChild(swiperWrapper);
    thumbsRoot.appendChild(prev);
    thumbsRoot.appendChild(next);
    gallery.appendChild(thumbsRoot);

    const [{ default: Swiper }, { Navigation, A11y }] = await Promise.all([
        import('swiper'),
        import('swiper/modules'),
    ]);

    thumbsRoot.swiper = new Swiper(thumbsRoot, {
        modules: [Navigation, A11y],
        slidesPerView: 'auto',
        spaceBetween: 8,
        watchOverflow: true,
        grabCursor: true,
        preventClicks: false,
        preventClicksPropagation: false,
        navigation: {
            nextEl: next,
            prevEl: prev,
        },
    });
}

/**
 * Punto de entrada. Observa el DOM porque Woo puede reemplazar la galería al elegir variación.
 */
export function initProductGalleryThumbs() {
    const run = () => {
        document.querySelectorAll('.woocommerce-product-gallery').forEach((gallery) => {
            mountGallery(gallery);
        });
    };

    run();

    const observer = new MutationObserver(run);
    observer.observe(document.body, { childList: true, subtree: true });
}
